import { Component, computed, EventEmitter, Input, model, OnDestroy, OnInit, Output } from '@angular/core';
import { SideNavService } from '../../../services/side-nav.service';
import { Program, SchedulingParams, SearchItem, SiteTopology, TopologyObject } from '../../../domain/cloud-topology';
import { ProgramService } from '../../../services/vpp/program.service';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup } from '@angular/forms';
import moment from 'moment-timezone';
import { forkJoin, last, Subject } from 'rxjs';
import { ScheduleService } from '../../../services/vpp/schedule.service';
import { takeUntil } from 'rxjs/operators';
import { TopologyService } from '../../../services/topology/topology.service';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

@Component({
  selector: 'app-control-selectors',
  templateUrl: './control-selectors.component.html',
  styleUrls: ['./control-selectors.component.scss'],
  standalone: false,
})
export class ControlSelectorsComponent implements OnInit, OnDestroy {

  constructor(private sideNavService: SideNavService,
              private programService: ProgramService,
              private scheduleService: ScheduleService,
              private topologyService: TopologyService,
              private router: Router,
              private route: ActivatedRoute) {
    this.filterEnabled = false;
  }

  params;
  @Input() filter: SchedulingParams;
  @Input() sites: SiteTopology[];
  @Input() programs: Program[];
  @Output() paramEvent = new EventEmitter<any>();

  developers: TopologyObject[];
  accounts: TopologyObject[];
  selectedSites: SearchItem[] = [];
  selectedAccounts: SearchItem[] = [];
  selectedPrograms: SearchItem[] = [];
  selectedDevelopers: SearchItem[] = [];
  selectedActivityTypes: SearchItem[] = [];
  selectedActivityStatuses: SearchItem[] = [];
  selectedBatterySystems: SearchItem[] = [];
  categories: any[];
  filterEnabled: boolean;
  favoriteTimezones: string[];
  timezones: string[];
  filteredTimezones: string[];
  selectedTimezone: string;
  timezoneSearch = '';
  selectedProjects = [];
  searchItems: any[] = [];
  readonly currentProject = model('');
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];

  range: FormGroup;
  private componentDestroyed$ = new Subject<void>();

  protected readonly last = last;

  ngOnInit() {
    this.friendlySortTimezones();
    this.filteredTimezones = [...this.timezones];


    forkJoin([
      this.scheduleService.getActivityStatuses(),
      this.scheduleService.getActivityTypes(),
      this.topologyService.getBatterySystems(),
    ]).pipe(takeUntil(this.componentDestroyed$)).subscribe(data => {
      const activityStatuses = [];
      const activityType = [];
      const batterySystems = [];
      for (const activityStatus of data[0]) {
        activityStatuses.push({
          id: activityStatus,
          name: activityStatus,
          type: 'statuses',
        });
      }

      if (data[1]) {
        for (const activityTypes of data[1]) {
          activityType.push({
            id: activityTypes,
            name: activityTypes,
            type: 'activities',
          });
        }
      }

      if (data[2]) {
        for (const batterySystem of data[2]) {
          batterySystems.push({
            id: batterySystem,
            name: batterySystem,
            type: 'batterySystems',
          });
        }
      }
      this.categories = [
        {
          id: 'developers',
          title: 'Developers',
          children: this.getDevelopers(),
        },
        {
          id: 'accounts',
          title: 'Accounts',
          children: this.getAccounts(),
        },
        {
          id: 'sites',
          title: 'Sites',
          children: this.getSites(),
        },
        {
          id: 'programs',
          title: 'Programs',
          children: this.getPrograms(),
        },
        {
          id: 'activities',
          title: 'Activity Types',
          children: activityType,
        },
        {
          id: 'statuses',
          title: 'Activity Status',
          children: activityStatuses,
        },
        {
          id: 'batterySystems',
          title: 'Battery Systems',
          children: batterySystems,
        },
      ];
    });
    this.setFilter();
  }

  private friendlySortTimezones() {
    // Default ordering: Selected tz, local time, then LA, NY, UTC, then the rest alphabetically.
    const local = moment.tz.guess();
    this.favoriteTimezones = ['America/Los_Angeles', 'America/New_York', 'UTC'];
    // Add local tz if not already in the list
    if (!this.favoriteTimezones.includes(local)) {
      this.favoriteTimezones.unshift(local);
    }
    // Add pre-selected tz if available & not in list
    const filteredTZ = this.filter.timezone;
    if (filteredTZ && !this.favoriteTimezones.includes(filteredTZ)) {
      this.favoriteTimezones.unshift(filteredTZ);
    }

    const allTimezones = moment.tz.names().filter(tz => !this.favoriteTimezones.includes(tz)).sort();
    this.timezones = [...this.favoriteTimezones, ...allTimezones];
    this.filteredTimezones = [...this.timezones]; // Initialize filtered list
  }

  filterTimezones() {
    const search = this.timezoneSearch.toLowerCase();

    if (search) {
      this.filteredTimezones = this.timezones.filter(tz => tz.toLowerCase().includes(search));
    } else {
      let filtered = [...this.timezones];

      const isSelectedFavorite = this.favoriteTimezones.includes(this.selectedTimezone);
      const isSelectedInFiltered = filtered.includes(this.selectedTimezone);

      if (this.selectedTimezone && isSelectedInFiltered && !isSelectedFavorite) {
        // Order: Selected Timezone -> Favorites -> Everything else
        filtered = [
          this.selectedTimezone,
          ...this.favoriteTimezones,
          ...filtered.filter(tz => tz !== this.selectedTimezone && !this.favoriteTimezones.includes(tz)),
        ];
      } else {
        // Order: Favorites -> Everything else
        filtered = [
          ...this.favoriteTimezones,
          ...filtered.filter(tz => !this.favoriteTimezones.includes(tz)),
        ];
      }
      this.filteredTimezones = [...new Set(filtered)]; // Ensure uniqueness
    }
  }


  onTimezoneSelected() {
    this.enableFilterButton();
    this.timezoneSearch = '';
    this.filterTimezones();
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }

  setFilter() {
    this.selectedProjects = [];
    if (this.filter.timezone) {
      this.selectedTimezone = this.filter.timezone;
    } else {
      this.selectedTimezone = moment.tz.guess();
    }

    if (this.filter.startDate && this.filter.stopDate) {
      this.range = new FormGroup({
        startDate: new FormControl(moment(this.filter.startDate).tz(this.selectedTimezone).format('YYYY-MM-DDT00:00:00Z')),
        endDate: new FormControl(moment(this.filter.stopDate).tz(this.selectedTimezone).format('YYYY-MM-DDT00:00:00Z')),
      });
    } else {
      this.range = new FormGroup({
        startDate: new FormControl(moment().tz(this.selectedTimezone).format('YYYY-MM-DDT00:00:00Z')),
        endDate: new FormControl(moment().add(1, 'days').tz(this.selectedTimezone).format('YYYY-MM-DDT00:00:00Z')),
      });
    }

    if (this.filter.developers.length > 0) {
      this.selectedDevelopers = [...this.selectedProjects, ...this.categories[0].children.filter((existingItem) => {
        return this.developers.some((filterItem) => {
          return filterItem == existingItem.id;
        });
      })];
    }

    if (this.filter.accounts.length > 0) {
      this.selectedProjects = [...this.selectedProjects, ...this.categories[1].children.filter((existingItem) => {
        return this.filter.accounts.some((filterItem) => {
          return filterItem == existingItem.id;
        });
      })];
    }

    if (this.filter.sites.length > 0) {
      this.selectedProjects = [...this.selectedProjects, ...this.categories[2].children.filter((existingItem) => {
        return this.filter.sites.some((filterItem) => {
          return filterItem == existingItem.id;
        });
      })];
    }

    if (this.filter.programs.length > 0) {
      this.selectedProjects = [...this.selectedProjects, ...this.categories[3].children.filter((existingItem) => {
        return this.filter.programs.some((filterItem) => {
          return filterItem == existingItem.id;
        });
      })];
    }

    if (this.filter.activityTypes.length > 0) {
      this.selectedProjects = [...this.selectedProjects, ...this.categories[4].children.filter((existingItem) => {
        return this.filter.activityTypes.some((filterItem) => {
          return filterItem == existingItem.id;
        });
      })];
    }

    if (this.filter.activityStatuses.length > 0) {
      this.selectedActivityStatuses = [...this.selectedProjects, this.categories[5].children.filter((existingItem) => {
        return this.filter.activityStatuses.some((filterItem) => {
          return filterItem == existingItem.id;
        });
      })];
    }

    if (this.filter.systemTypes.length > 0) {
      this.selectedBatterySystems = [...this.selectedProjects, this.categories[6].children.filter((existingItem) => {
        return this.filter.systemTypes.some((filterItem) => {
          return filterItem == existingItem.id;
        });
      })];
    }
  }

  applyFilter() {
    this.selectedSites = [];
    this.selectedAccounts = [];
    this.selectedDevelopers = [];
    this.selectedPrograms = [];
    this.selectedActivityTypes = [];
    this.selectedActivityStatuses = [];
    this.selectedBatterySystems = [];

    for (const selectedItem of this.selectedProjects) {
      if (selectedItem.type == 'developers') {
        this.selectedDevelopers.push(selectedItem.id);
      }

      if (selectedItem.type == 'accounts') {
        this.selectedAccounts.push(selectedItem.id);
      }

      if (selectedItem.type == 'sites') {
        this.selectedSites.push(selectedItem.id);
      }

      if (selectedItem.type == 'programs') {
        this.selectedPrograms.push(selectedItem.id);
      }

      if (selectedItem.type == 'activities') {
        this.selectedActivityTypes.push(selectedItem.id);
      }

      if (selectedItem.type == 'statuses') {
        this.selectedActivityStatuses.push(selectedItem.id);
      }

      if (selectedItem.type == 'batterySystems') {
        this.selectedBatterySystems.push(selectedItem.id);
      }
    }
    const startDate = moment(this.range.get('startDate').value).format('YYYY-MM-DDT00:00:00Z');
    const endDate = moment(this.range.get('endDate').value).format('YYYY-MM-DDT00:00:00Z');
    this.params = {
      sites: this.selectedSites,
      developers: this.selectedDevelopers,
      accounts: this.selectedAccounts,
      programs: this.selectedPrograms,
      activityTypes: this.selectedActivityTypes,
      activityStatuses: this.selectedActivityStatuses,
      systemTypes: this.selectedBatterySystems,
      startDate: startDate,
      stopDate: endDate,
      timezone: this.selectedTimezone,
    };
    this.paramEvent.emit(this.params);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: { filter: JSON.stringify(this.params) },
    }).then(() => (this.filterEnabled = false));
  }

  enableFilterButton() {
    this.filterEnabled = true;
  }

  compareAccounts = (item, selected) => {
    if (item.name && selected.name) {
      return item.name === selected.name;
    }

    if (selected.type && item.type) {
      return item.type === selected.type;
    }
    return false;
  };

  private getDevelopers() {
    const searchableDevelopers = [];
    this.developers = this.sideNavService.getDevelopers();
    for (const developer of this.developers) {
      searchableDevelopers.push({
        id: developer.id,
        name: developer.name,
        type: 'developers',
      });
    }

    return searchableDevelopers;
  }

  private getAccounts() {
    const searchableAccounts = [];
    this.accounts = this.sideNavService.getAccounts();
    for (const account of this.accounts) {
      searchableAccounts.push({
        id: account.id,
        name: account.name,
        type: 'accounts',
      });
    }

    return searchableAccounts;
  }

  private getSites() {
    const searchableSites = [];
    for (const site of this.sites) {
      searchableSites.push({
        id: site.id,
        name: site.name,
        type: 'sites',
      });
    }

    return searchableSites;
  }

  private getPrograms() {
    const searchablePrograms = [];
    for (const program of this.programs) {
      searchablePrograms.push({
        id: program.id,
        name: program.name,
        type: 'programs',
      });
    }

    return searchablePrograms;
  }

  filteredChildren(groupId: string) {
    return computed(() => {
      const currentProject = this.currentProject().toLowerCase();
      return !!currentProject
        ? this.categories
          .filter(c => groupId === c.id)
          .map(category => category.children)[0]
          .filter(child => child.name.toLowerCase().includes(currentProject.toLowerCase()))
        : this.categories.slice().filter(c => groupId === c.id).map(category => category.children)[0];
    });
  }

  removeProject(project: any): void {
    this.selectedProjects = this.selectedProjects.filter(s => s.id !== project.id);
    this.enableFilterButton();
  }

  addProject(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    // Add our fruit
    if (value) {
      this.selectedProjects.push(value);
      this.enableFilterButton();
    }

    // Clear the input value
    this.currentProject.set('');
  }

  selectProject(event: MatAutocompleteSelectedEvent) {
    this.selectedProjects.push(event.option.value);
    this.enableFilterButton();
    this.currentProject.set('');
    event.option.deselect();
  }
}
